import { DefaultButton, Spinner, SpinnerSize } from '@fluentui/react';
import React, { useState, useEffect } from 'react'
import { ChangeStatusActionType, LocationType, PermissionRole, ProvisioningStatus, SiteStatus, SiteType } from '../../../GlobalConst/globalEnums';
import { useIsMounted, addDays } from '../../../helpers/HelperMethods';
import Api from '../../../services/Api'
import { ModalDialog } from '../../formFields/Dialog';
import SecuredComponent from './SecuredComponent';


const DeleteRestoreBtn = ({site, myOwnSite, changeStatus, currentUser}) => {

    const isMsTeams = site.siteTemplate.siteType === SiteType.team;
    const isOnPremise = site.farmLocation.locationType === LocationType.onPremise;
    const isMs365Archived = site.provisioningStatus === ProvisioningStatus.ms365Archived;
    const isButtonsVisible = ((site.owner && site.owner.userId === currentUser.id) || currentUser.role === PermissionRole.administrator) && site.siteTemplate.siteType !== SiteType.group;
    const isArchivedForOver7Days = site.ms365ArchiveDate != null && addDays(site.ms365ArchiveDate, 7) < new Date();
    const isHubSite = site.isHubSite === true;
    const IsMs365ArchiveSupported = !isHubSite && site.spTemplate && (site.spTemplate.isMs365ArchiveSupported != null ? site.spTemplate.isMs365ArchiveSupported : true);
    const entryType = isMsTeams ? "team" : "site";

    const isHardDeleted = site.provisioningStatus === ProvisioningStatus.permanentDeletion;
    const isSoftDeleted = ((site.siteStatus === SiteStatus.disabled && !site.keepReadOnly) || site.siteStatus === SiteStatus.deleted) && !isMs365Archived;
    const isTeamArchived = isMsTeams && site.siteStatus === SiteStatus.archived && site.provisioningStatus === ProvisioningStatus.readOnly && !site.keepReadOnly;
    const isEmptyOwner = !(site.owner && site.owner.isDeleted !== true);
    
    const [isLoading, setIsLoading] = useState(false);
    const [isGroupRestorable, setGroupIsRestorable] = useState(true);
    const [isSiteRestorable, setIsSiteRestorable] = useState(!isHardDeleted || !isOnPremise);
    
    const isMounted = useIsMounted();

    useEffect(() => {
        if (isHardDeleted && !isOnPremise) {
            setIsLoading(true);

            if (site.isGroupConnected) {
                checkIsGroupRestorable();
            }
            else {
                setGroupIsRestorable(true);
                checkIsSiteRestorable();
            }
        }
    }, []);

    const checkIsGroupRestorable = () => {
        Api.get(`EntryValidation/IsGroupRestorable?groupId=${site.groupId}`)
            .then(res => res.data)
            .then(result => {
                if (isMounted()) {
                    setGroupIsRestorable(result);
                    if (result) {
                        setIsSiteRestorable(true);
                        setIsLoading(false);
                    } else {
                        checkIsSiteRestorable();
                    }
                }
            });
    }

    const checkIsSiteRestorable = () => {
        Api.get(`EntryValidation/IsSiteRestorable?siteUrl=${site.url}`)
            .then(res => res.data)
            .then(result => {
                if (isMounted()) {
                    setIsSiteRestorable(result);
                    setIsLoading(false);
                }
            });
    }

    const styles = {
        button: {
            marginLeft: "16px",
            padding: "5px"
        }
    };
    
    return (
        site.url && site.farmLocation.enableSiteEdit && isButtonsVisible &&
        (
            <div style={{display: 'flex'}}>
                {
                    !isHubSite && isTeamArchived &&
                        <SecuredComponent
                            readSecurityLevel='Contributor'
                            unlimitedAccess={myOwnSite}>
                            {
                                <DefaultButton disabled={isEmptyOwner} title={isEmptyOwner ? "Administrator is not valid" : ""} style={styles.button} onClick={() => changeStatus(ChangeStatusActionType.restore)}>Unarchive Team</DefaultButton>
                            }
                        </SecuredComponent>
                }
                {
                    (!isHubSite &&
                        (isSoftDeleted
                            ? (
                                <SecuredComponent
                                    readSecurityLevel='Contributor'
                                    unlimitedAccess={site.siteStatus !== SiteStatus.disabled && myOwnSite}>
                                    {
                                        isLoading
                                            ? <Spinner style={{ marginRight: '70px' }} size={SpinnerSize.medium} />
                                            : <div title={!isSiteRestorable ? `Site has been permanently deleted with no possibility to restore from recycle bin.` : ""}>
                                                {
                                                    isSiteRestorable && !isGroupRestorable
                                                        ? <ModalDialog
                                                            title={"Restore Team without Group"}
                                                            text={"The O365 Group associated to this Site cannot be restored. Do you want to restore SharePoint Site without Group?"}
                                                            buttonName={"Restore Team"}
                                                            actionConfirmed={() => changeStatus(ChangeStatusActionType.restore)}
                                                        />
                                                        : <DefaultButton className="edit-icon" disabled={!isSiteRestorable} onClick={() => changeStatus(ChangeStatusActionType.restore)}>{`Restore ${isMsTeams ? "Team" : "Site"}`}</DefaultButton>
                                                }
                                            </div>
                                    }
                                </SecuredComponent>
                            )
                            :
                            (site.siteStatus !== SiteStatus.deleted && !site.doNotDelete &&
                                <ModalDialog
                                    btnClassName={styles.button}
                                    title={`Delete ${isMsTeams ? "Team" : "Site"}`}
                                    text={isMsTeams ?
                                        `Are you sure you want to delete this team and all associated content (O365 group, Teams, SharePoint, etc.)? Your team will be set to read-only mode for 1 month, then ${IsMs365ArchiveSupported ? "archived for 5 months" : "set to no-access for 5 months"} and afterwards all components will be permanently deleted. This action is irreversible.`
                                        :
                                        `Are you sure you want to delete this site?  Your site will be set to read-only mode for 1 month, then, set to no-access for 2 months, then ${IsMs365ArchiveSupported ? "archived for 3 months" : "set to no-access for 3 months"} and afterwards will be permanently deleted. This action is irreversible.`}
                                    buttonName={`Delete ${isMsTeams ? "Team" : "Site"}`}
                                    actionConfirmed={() => changeStatus(ChangeStatusActionType.delete)}
                                />
                            )
                            
                        )
                    )
                }
                {!isHardDeleted && !isOnPremise &&
                    (isMs365Archived
                    ? <ModalDialog
                        btnClassName = {styles.button}
                        title={"MS 365 Restore"}
                        text={(isArchivedForOver7Days ? `This ${entryType} is archived for over 7 days, reactivation will be charged as per Microsoft 365 Archive Billing, and will take time.` : `This ${entryType} was archived in the last 7 days and can be reactivated without a fee.`) + (currentUser.role !== PermissionRole.administrator ? ' Restoration needs approval from Quota Application Manager.' : '')}
                        buttonName={"MS 365 Restore"}
                        actionConfirmed={(comment) => changeStatus(ChangeStatusActionType.ms365Restore, comment)}
                        textField={currentUser.role !== PermissionRole.administrator}
                        />
                    : <ModalDialog
                        btnClassName = {styles.button}
                        title={"MS 365 Archive"}
                        text={
                            <div>
                                <p>
                                    {
                                        `The ${entryType} and its contents cannot be accessed when a ${entryType} is archived. The ${entryType} needs to be reactivated if it needs to be accessed. Archived ${entryType} can be reactivated instantaneously, without any additional charges within 7 days of the action. After 7 days, reactivations will be charged as per Microsoft 365 Archive Billing, and will take time.`
                                    }
                                </p>
                                <p>
                                    {
                                        `${site.isGroupConnected ? "Your selection includes 1 site that belongs to a Microsoft 365 Group. Archiving this site will archive site contents but other associated group elements like Teams channel, Exchange mailboxes will continue being active. Users will see error messages when trying to access archived content." : ""}`
                                    }
                                </p>
                            </div>
                        }
                        buttonName={"MS 365 Archive"}
                        actionConfirmed={() => changeStatus(ChangeStatusActionType.ms365Archive)}
                        btnDisabled={!IsMs365ArchiveSupported}
                        tooltip={IsMs365ArchiveSupported ? null : (isHubSite ? "Hub Sites archiving is not supported" : "Microsoft 365 Archive feature is currently not supported for this template")}
                        />
                    )
                }
            </div>
        )
    );
}

export default DeleteRestoreBtn


// WEBPACK FOOTER //
// ./src/components/EditSite/SiteEditElements/DeleteRestoreBtn.js